import React, { createContext, useContext, useState, useEffect } from "react"
import dayjs from "dayjs"
import { cn } from "lib/utils"
import SectionHeaderRender from "lib/units/element/SectionHeader/SectionHeaderRender"
import { useRenderStyling } from "lib/hooks/useRenderStyling";
import { getFromRp, formatter } from "./utils"
import { DetailScreenActionsInfo, DetailScreenCCInfo, DetailScreenCustomerInfo, DetailScreenTrxInfo, DetailScreenWrapper } from "./detailScreens"

const DetailScreenContext = createContext({});
export const useDetailScreenCtx = () => useContext(DetailScreenContext);

export default function DetailScreensRender({ 
  mode="preview",
  renderPkg,
  visualEffects=[] 
}:{ 
  mode?:string
  renderPkg?:any
  visualEffects?:string[] 
}){

  const settings = renderPkg.dispute.elements.find((setting:any) => setting.refstr === "detail_screens").settings || {}
  
  const [ preppedData, setPreppedData ] = useState<any>({ ready: false })
  const [ spotlightBox, setSpotlightBox ] = useState<any>({ ready: false }) 

  const capitalize =(str:string) => str.charAt(0).toUpperCase() + str.slice(1)

  const caseId = getFromRp(renderPkg.clientdata, "Chargeback.ProcessorCaseId")
  const cardData = {
    authCode: getFromRp(renderPkg.clientdata, "RepresentmentTransactionInfo.AuthCode"),
    ccNumber: getFromRp(renderPkg.clientdata, "RepresentmentTransactionInfo.CardNumber"),
    ccExpDate: getFromRp(renderPkg.clientdata, "RepresentmentTransactionInfo.ExpDate"),
    cardBrand: getFromRp(renderPkg.clientdata, "RepresentmentTransactionInfo.CardBrand"),
    descriptor: getFromRp(renderPkg.clientdata, "Chargeback?.PriorRefund?.PriorAlert?.Descriptor") || ""
  }
  const customer = {
    billing: {
      name: getFromRp(renderPkg.clientdata, "OrderInfo.BillingContact.Name"),
      address: getFromRp(renderPkg.clientdata, "OrderInfo.BillingContact.Address"),
      city: getFromRp(renderPkg.clientdata, "OrderInfo.BillingContact.City"),
      state: getFromRp(renderPkg.clientdata, "OrderInfo.BillingContact.State"),
      zip: getFromRp(renderPkg.clientdata, "OrderInfo.BillingContact.Zip"),
      country: getFromRp(renderPkg.clientdata, "OrderInfo.BillingContact.Country"),
      phone: getFromRp(renderPkg.clientdata, "OrderInfo.Phone")
    },
    shipping: {
      name: getFromRp(renderPkg.clientdata, "OrderInfo.ShippingContact.Name") || getFromRp(renderPkg.clientdata, "OrderInfo.BillingContact.Name"),
      address: getFromRp(renderPkg.clientdata, "OrderInfo.ShippingContact.Address") || getFromRp(renderPkg.clientdata, "OrderInfo.BillingContact.Address"),
      city: getFromRp(renderPkg.clientdata, "OrderInfo.ShippingContact.City") || getFromRp(renderPkg.clientdata, "OrderInfo.BillingContact.City"),
      state: getFromRp(renderPkg.clientdata, "OrderInfo.ShippingContact.State") || getFromRp(renderPkg.clientdata, "OrderInfo.BillingContact.State"),
      zip: getFromRp(renderPkg.clientdata, "OrderInfo.ShippingContact.Zip") || getFromRp(renderPkg.clientdata, "OrderInfo.BillingContact.Zip"),
      country: getFromRp(renderPkg.clientdata, "OrderInfo.ShippingContact.Country") || getFromRp(renderPkg.clientdata, "OrderInfo.BillingContact.Country"),
      phone: getFromRp(renderPkg.clientdata, "OrderInfo.Phone")
    }
  }

  const prepData = () => {
    const transactions = getFromRp(renderPkg.clientdata, "AdditionalTransactions") || []
    const screens = transactions.map( (trx:any) => ({
      trxinfo: {
        merchant: trx.TransactionDetails.CompanyName,
        trxId: trx.TransactionDetails.ExternalTransactionId,
        entryMethod: "Keyed",
        respCode: "100",
        date: trx.TransactionDetails.TransactionDate,
        trxType: `${trx.TransactionDetails.ActionType.charAt(0).toUpperCase()}${trx.TransactionDetails.ActionType.slice(1)}`,
        trxAmount: trx.TransactionDetails.Amount,
        trxCurrency: trx.TransactionDetails.Currency,
        status: "Complete"
      },

      ccInfo: {
        ccNumber: cardData.ccNumber,
        ccType: cardData.cardBrand,
        cvvStatus: (trx.TransactionDetails.CVSMatchCode_Id === "M" || trx.TransactionDetails.CVSMatchCode_Id == "1") ? "CVV2/CVC2 Match" : "",
        ccExp: cardData.ccExpDate,
        avsStatus: getFromRp(renderPkg.clientdata, "RepresentmentTransactionInfo.TransactionAVSCode"),
        avsDescription: () => {
          const avsCode = getFromRp(renderPkg.clientdata, "RepresentmentTransactionInfo.TransactionAVSCode") || "";
          const description = getFromRp(renderPkg.clientdata, "RepresentmentTransactionInfo.AVSCodeDescription") || "";
          const suffix = (avsCode.length > 0) ? (` (${avsCode})`) : "";
          return `${description}${suffix}`;
        },
        authCode: cardData.authCode,
      },

      billing: customer.billing,
      shipping: customer.shipping,

      additional: {
        orderId: trx.TransactionDetails.MerchantReference,
      },

      action: {
        success: trx.TransactionDetails.Success,
        actionType: trx.TransactionDetails.ActionType,
        date: trx.TransactionDetails.TransactionDate,
        amt: trx.TransactionDetails.Amount,
        currency: getFromRp(renderPkg.clientdata, "Chargeback.Currency"),
      }
    }))
    setPreppedData({ ready: true, screens })
  }

  const screenWrapper = useRenderStyling("w-full flex flex-col mt-10", "styleObj")

  // useEffect(() => {
  //   if( preppedData.ready ){
  //     getMeasurements()
  //   }
  // },[preppedData])

  useEffect(() => {
    prepData()
  },[])

  if( !preppedData.ready ) return <></>
  return (
    <DetailScreenContext.Provider value={{ visualEffects }}>
      
    <div style={{ width: "700px", display: "flex", flexDirection: "column", marginBottom: "1rem" }}>
      <SectionHeaderRender title="Transaction Information" className="mb-[-20px]" />
      
      { preppedData.screens.length > 0 && preppedData.screens.map( (screen:any, index:number) => (
        <DetailScreenWrapper key={index}>
          <div {...screenWrapper}>
            <DetailScreenTrxInfo screen={screen} renderPkg={renderPkg} />
            <DetailScreenCCInfo screen={screen} renderPkg={renderPkg} />
            <DetailScreenCustomerInfo screen={screen} renderPkg={renderPkg} />
            <DetailScreenActionsInfo screen={screen} renderPkg={renderPkg} />
          </div>
        </DetailScreenWrapper>
      ))}
    </div>

    </DetailScreenContext.Provider>
  )
}
